import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import {
	Variable,
	ancestor_variable_path,
	ancestor_named_object_path,
	optional_variable_assignment,
	variable_assignment,
} from "../state/Variable";
import { iReference } from "../state/Reference";
import * as Rule from "../state/Rule";
import { client_state, getUserNameOrDefault } from "../state/ClientState";
import { getStickyValue } from "./mapCommandParameters";
import * as uuid from "./uuid";
import { pad } from "../utils";
import * as autoIncrement from "../state/autoIncrement";
import { getCurrentDateAndTime } from "../calendar/current";
import { jsdate_2_julian_date, julian_datetime_2_js_date } from "../calendar/conversion";
import { serializeNumber } from "../state 2 interface/serializeNumber";
import {
	createExpressionBuilder,
	lambda,
	DefaultExpressionBuilder,
	ScalarProvider,
	ScalarConsumer,
} from "../../../../../stream";

import { Pending, Unknown, Failed, Result } from "../Status";
import * as step from "./step";

export type CsObject =
	| api_cs.Cnode
	| api_cs.Cstate_group__type__property
	| api_cs.Ccollection
	| api_cs.Ctext__type__property
	| api_cs.Cnumber__type__property
	| api_cs.Cfile__type__property;
type CsProperty =
	| api_cs.Ctext__type__property
	| api_cs.Cnumber__type__property
	| api_cs.Cfile__type__property
	| api_cs.Cgroup__type__property
	| api_cs.Cstate_group__type__property
	| api_cs.Ccollection;

export type Style =
	| [
			"fixed",
			{
				style: api_app.Tui_style["style"];
			}
	  ]
	| [
			"color",
			{
				value: string;
			}
	  ];
export type StateChoice = api_app.Cstates__state_group;
export type FileToken = {
	token: string;
	extension: string;
};
export type StringLike = string | { toString: () => string };
type ScalarValue = StringLike | number | Style | StateChoice | FileToken;
type ScalarValueExpressionTarget = api_cs.Cproperty | undefined;

export type UIContext = [Result<api_cs.Cnode>, Variable | null, ScalarValueExpressionTarget?];

function property_step(root: api_app.Cproperty_step): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsObject>> {
	const $type = root.properties.property.ref.properties.type.state;
	switch ($type.name) {
		case "text":
			return step.property($type.node);
		case "number":
			return step.property($type.node);
		case "file":
			return step.property($type.node);
		case "group":
			return step.groupNode($type.node);
		case "state group":
			return step.property($type.node);
		case "collection":
			return step.property($type.node);
	}
}

function dependency_step(
	root: api_app.Cdependency_step
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	const $type = root.properties.type.state;
	switch ($type.name) {
		case "reference":
			return step.reference($type.node.properties.property.inferences.has_reference());
		case "reference rule":
		case "state context rule":
			return step.rule($type.node.properties.rule.properties.rule.ref);
	}
}

export function context_object_step(
	root: api_app.Ccontext_object_step
): DefaultExpressionBuilder<Result<CsObject> | Variable | undefined, Result<CsObject>> {
	switch (root.properties.context.state.name) {
		case "this":
			return createExpressionBuilder<Result<CsObject>>();
		case "variable": {
			const variable_step = root.properties.context.state.node;
			const $name = variable_step.properties.step.properties.name.state;
			switch ($name.name) {
				case "explicit":
					return lambda(
						(variable: Variable) =>
							ancestor_named_object_path($name.node.properties.head, variable).value as api_cs.Cnode
					);
				case "implicit":
					return lambda((variable: Variable) => variable.value as api_cs.Cnode);
			}
			break;
		}
		case "dynamic user":
			return dynamicUser();
	}
}

export function ancestor_node_path(
	root: api_app.Cancestor_node_path
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	const $has_steps = root.properties.has_steps.state;
	switch ($has_steps.name) {
		case "no":
			return createExpressionBuilder();
		case "yes":
			return step.parent().pipe(ancestor_node_path($has_steps.node.properties.tail));
	}
}

export function entry_reference_definition(
	root: api_app.Centry_reference_definition
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ccollection>> {
	const collection_stream = context_object_step(root.properties.collection_path.properties.head).pipe(
		object_path_tail(root.properties.collection_path.properties.path)
	);
	switch (root.properties.type.state.name) {
		case "sibling":
			return (collection_stream as DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>>)
				.pipe(step.parent())
				.pipe(step.property(root.properties.type.state.node.output.collection()));
		case "simple":
			return collection_stream as DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ccollection>>;
	}
}

export function object_path_tail(
	root: api_app.Cobject_path_tail
): DefaultExpressionBuilder<Result<CsObject>, Result<CsObject>> {
	const $has_steps = root.properties.has_steps.state;
	switch ($has_steps.name) {
		case "no":
			return createExpressionBuilder();
		case "yes": {
			const $type = $has_steps.node.properties.type.state;
			switch ($type.name) {
				case "parent":
					return step.parent().pipe(object_path_tail($has_steps.node.properties.tail));
				case "property value":
					return property_step($type.node.properties.property).pipe(object_path_tail($has_steps.node.properties.tail));
				case "dependency":
					return dependency_step($type.node.properties.dependency).pipe(
						object_path_tail($has_steps.node.properties.tail)
					);
				case "reference set":
					throw new Error("Unsupported 'Reference set' step");
				case "state":
					return step.state($type.node.properties.state.ref).pipe(object_path_tail($has_steps.node.properties.tail));
			}
		}
	}
}

export function descendant_object_path(
	root: api_app.Cdescendant_object_path
): DefaultExpressionBuilder<Result<CsObject>, Result<CsObject>> {
	return object_path_tail(root.properties.path);
}

export function where_rule_context(): DefaultExpressionBuilder<api_cs.Crules, api_cs.Cnode> {
	return lambda((rule: api_cs.Crules) => rule.parent.input.definer().variant.definition.component_root);
}

export function where_rule_variable_context(
	definer: api_app.Cwhere_rules_definer
): DefaultExpressionBuilder<api_cs.Crules, Result<Variable>> {
	switch (definer.variant.name) {
		case "text": {
			const $has_assignment = definer.variant.definition.properties.assignment.properties.has_assignment.state;
			switch ($has_assignment.name) {
				case "no":
					throw new Error("Unexpected where rule variable context");
				case "yes":
					return lambda((rule: api_cs.Crules) =>
						createExpressionBuilder(iReference(rule.parent.input.definer().cast("reference")).referencedNode).pipe(
							step.sync((node) => variable_assignment($has_assignment.node.properties.assignment, null, node))
						)()
					);
			}
			break;
		}
		case "state":
			throw new Error("Unexpected where rule variable context");
	}
}

export function where_rule_object_path(
	root: api_app.Cwhere_rule_object_path
): DefaultExpressionBuilder<api_cs.Crules, Result<CsObject>> {
	const $context = root.properties.context.state;
	switch ($context.name) {
		case "this": {
			const $step = $context.node.properties.head;
			switch ($step.properties.context.state.name) {
				case "this":
					return where_rule_context().pipe(object_path_tail(root.properties.path));
				case "variable": {
					const variable_step = $step.properties.context.state.node;
					const $name = variable_step.properties.step.properties.name.state;
					switch ($name.name) {
						case "explicit":
							return where_rule_variable_context(root.input.definer())
								.pipe(
									step.sync(
										(variable) => ancestor_named_object_path($name.node.properties.head, variable).value as api_cs.Cnode
									)
								)
								.pipe(object_path_tail(root.properties.path));
						case "implicit":
							return where_rule_variable_context(root.input.definer())
								.pipe(step.sync((variable) => variable.value as api_cs.Cnode))
								.pipe(object_path_tail(root.properties.path));
					}
					break;
				}
				case "dynamic user":
					return dynamicUser().pipe(object_path_tail(root.properties.path));
			}
			break;
		}
		case "sibling rule":
			return lambda((rule: api_cs.Crules) => {
				const sibling = rule.parent.properties.rules.get($context.node.properties.rule.entry)!;
				return Rule.node(sibling);
			}).pipe(object_path_tail(root.properties.path));
	}
}

function descendant_base_property_path(
	root: api_app.Cdescendant_base_property_path,
	def: api_app.Ccollection__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ccollection>>;
function descendant_base_property_path(
	root: api_app.Cdescendant_base_property_path
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsProperty>>;
function descendant_base_property_path(
	root: api_app.Cdescendant_base_property_path
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsProperty>> {
	const type = root.properties.property.ref.properties.type;
	return (
		object_path_tail(root.properties.head) as DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>>
	).pipe(step.property(type.state.node));
}

function node_step(
	root: api_app.Cnode_step__application
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	const type = root.properties.type.state;
	switch (type.name) {
		case "parent":
			return step.parent();
		case "group":
			return step.groupNode(type.node.properties.group.ref);
		case "reference":
			return step.reference(type.node.properties.text.inferences.reference());
		case "state":
			return step.property(type.node.properties.state_group.ref).pipe(step.state(type.node.properties.state.ref));
		case "property rule":
			return step.rule(type.node.properties.rule.ref);
		case "state context rule":
			return step.rule(type.node.properties.rule.ref);
	}
}

export function node_path_tail(
	root: api_app.Cnode_path_tail
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	const has_steps = root.properties.has_steps.state;
	switch (has_steps.name) {
		case "no":
			return createExpressionBuilder();
		case "yes":
			return node_step(has_steps.node.properties.step).pipe(node_path_tail(has_steps.node.properties.tail));
	}
}

export function dynamicUser(): DefaultExpressionBuilder<any, Result<api_cs.Cnode>> {
	const app_root = client_state.input.application;
	const user_name = getUserNameOrDefault();
	if (user_name !== undefined) {
		switch (app_root.properties.has_dynamic_users.state.name) {
			case "no":
				return lambda(() => Unknown);
			case "yes": {
				const dynamic_users = app_root.properties.has_dynamic_users.state.node;
				return createExpressionBuilder().pipe(() =>
					descendant_base_property_path(
						dynamic_users.properties.users_collection_path,
						dynamic_users.properties.users_collection_path.inferences.collection()
					).pipe(step.entry(user_name))(client_state.properties.root)
				);
			}
		}
	} else {
		return lambda(() => Unknown);
	}
}

function context_node_path(
	root: api_app.Ccontext_node_path
): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	const $context = root.properties.context.state;
	switch ($context.name) {
		case "this":
			return createExpressionBuilder<UIContext>().pipe(([this_node]) => this_node);
		case "dynamic user":
			return dynamicUser();
		case "variable": {
			const $name = $context.node.properties.name.state;
			switch ($name.name) {
				case "explicit":
					return lambda(
						([, variable]) => ancestor_variable_path($name.node.properties.head, variable!).value as api_cs.Cnode
					);
				case "implicit":
					return lambda(([, variable]) => variable!.value as api_cs.Cnode);
			}
		}
	}
}

export function node_path(root: api_app.Cnode_path): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	return context_node_path(root.properties.head).pipe(node_path_tail(root.properties.tail));
}

export function singular_node_path(
	root: api_app.Csingular_node_path
): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	return node_path(root.properties.path);
}

function conditional_node_path(
	root: api_app.Cconditional_node_path
): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	return node_path(root.properties.path);
}

export function ui_node_path(root: api_app.Cui_node_path): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	return node_path(root.properties.path);
}

export function ui_parametrized_node_path(
	root: api_app.Cui_parametrized_node_path
): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	return node_path(root.properties.path.properties.path);
}

export function node_expression(
	root: api_app.Cnode_expression
): DefaultExpressionBuilder<UIContext, Result<api_cs.Cnode>> {
	const $type = root.properties.type.state;
	switch ($type.name) {
		case "equality":
			return lambda(($) =>
				conditional_node_path(root.properties.node_path)
					.and(conditional_node_path($type.node.properties.expected_node_path)($))
					.pipe(([left_node, right_node]) => {
						if (left_node === Failed || right_node === Failed) {
							return Failed;
						} else if (left_node === Pending || right_node === Pending) {
							return Pending;
						} else if (left_node === Unknown || right_node === Unknown) {
							return Unknown;
						} else if (left_node.is(right_node)) {
							return left_node;
						} else {
							return Failed;
						}
					})($)
			);
		case "existence":
			return conditional_node_path(root.properties.node_path);
		case "containment": {
			const key_stream = conditional_node_path($type.node.properties.key_path.properties.path).pipe(
				step.textValue($type.node.properties.key_path.properties.text.ref)
			);
			return lambda(($) =>
				conditional_node_path(root.properties.node_path)
					.pipe(step.property($type.node.properties.collection.ref))
					.pipe(step.entry(key_stream($)))($)
			);
		}
	}
}

export function ui_scalar_default<T extends ScalarValue>(
	root: api_app.Cui_scalar_default
): DefaultExpressionBuilder<UIContext, Result<T>> {
	let fallback: DefaultExpressionBuilder<UIContext, Result<T>>;
	switch (root.properties.has_fallback.state.name) {
		case "yes":
			fallback = ui_scalar_default<T>(root.properties.has_fallback.state.node.properties.fallback);
			break;
		case "no":
			fallback = lambda(() => Failed);
	}
	return createExpressionBuilder<UIContext>().pipe(($) =>
		ui_expression<T>(root.properties.expression).pipe((value) => {
			if (value === Pending) {
				return Pending;
			} else if (value === Failed || value === Unknown) {
				return fallback($);
			} else {
				return value;
			}
		})($)
	);
}

export function ui_expression<T extends ScalarValue>(
	root: api_app.Cui_expression
): DefaultExpressionBuilder<UIContext, Result<T>> {
	const $operation = root.properties.operation.state;
	switch ($operation.name) {
		case "state switch":
			return lambda(($) =>
				ui_parametrized_node_path($operation.node.properties.path.properties.path)
					.pipe(step.selectedState($operation.node.properties.path.properties.stategroup.ref))
					.pipe(
						step.sync((state) => {
							const state_switch = $operation.node.properties.states.get(state.key.entry)!;
							return ui_expression<T>(state_switch.properties.expression)([
								$[0],
								optional_variable_assignment(state_switch.properties.variable_assignment, $[1], state.properties.node),
								$[2],
							]);
						})
					)($)
			);
		case "node switch":
			return lambda(($) => {
				return node_expression($operation.node.properties.expression).pipe((node) => {
					switch (node) {
						case Pending:
							return Pending;
						case Unknown:
							return Unknown;
						case Failed:
							return ui_expression<T>($operation.node.properties.cases.properties.none_case.properties.expression)($);
						default:
							return ui_expression<T>($operation.node.properties.cases.properties.node_case.properties.expression)([
								$[0],
								optional_variable_assignment(
									$operation.node.properties.cases.properties.node_case.properties.variable_assignment,
									$[1],
									node
								),
								$[2],
							]);
					}
				})($);
			});
		case "walk":
			throw new Error("unexpected expression operation");
		case "update properties":
			throw new Error("unexpected expression operation");
		case "execute operation":
			throw new Error("unexpected expression operation");
		case "produce value":
			switch ($operation.node.properties.value.state.name) {
				case "object":
					throw new Error("unexpected expression operation");
				case "state":
					throw new Error("unexpected expression operation");
				case "scalar":
					return ui_scalar_value_expression<T>($operation.node.properties.value.state.node.properties.expression);
			}
			break;
		case "entry list":
			throw new Error("unexpected expression operation");
		case "entry":
			throw new Error("unexpected expression operation");
		case "ignore":
			return lambda(() => Unknown);
	}
}

function singular_conversions(conversion: api_app.Csingular_conversions, value: number): number {
	return conversion.properties.type.switch({
		factor: ($) => {
			const factor = $.properties.factor.value * Math.pow($.properties.base.value, $.properties.exponent.value);
			return $.properties.invert.switch({
				no: value * factor,
				yes: value / factor,
			});
		},
		base: ($) => {
			const new_value =
				value + $.properties.offset.value * Math.pow($.properties.base.value, $.properties.exponent.value);
			return $.properties.unit_conversion.switch({
				no: () => new_value,
				yes: ($) => singular_conversions($.properties.conversion_rule.ref, new_value),
			});
		},
	});
}

export function ui_scalar_value_expression<T extends ScalarValue>(
	root: api_app.Cui_scalar_value_expression
): DefaultExpressionBuilder<UIContext, Result<T>> {
	switch (root.properties.type.state.name) {
		case "text": {
			const text__type = root.properties.type.state.node;
			switch (text__type.properties.type.state.name) {
				case "static": {
					const value = text__type.properties.type.state.node.properties.value as T;
					return lambda(() => value);
				}
				case "guid":
					return lambda(() => ({ toString: () => uuid.generate() } as T));
				case "auto increment": {
					const $ = text__type.properties.type.state.node;
					return lambda(([this_node]) => this_node).pipe(
						step.sync((node) => autoIncrement.property($, node) as ScalarProvider<Result<T>>)
					);
				}
				case "number to text": {
					const $ = text__type.properties.type.state.node;
					return ui_scalar_value_expression<number>($.properties.expression).pipe(
						step.sync((result) => {
							const serialized_value = serializeNumber(
								result,
								0, //TODO: the number should take number conversion into account
								$.properties.numerical_type.ref,
								undefined,
								true
							);
							return $.properties.pad.switch({
								no: serialized_value,
								yes: ($) => pad(serialized_value, $.properties.size.value, $.properties.character),
							}) as T;
						})
					);
				}
				case "concatenation": {
					const $ = text__type.properties.type.state.node;
					return ui_scalar_value_expression_list<StringLike>($.properties.list).pipe(
						step.sync((values) => ({ toString: () => values.map((val) => val.toString()).join("") } as T))
					);
				}
			}
			break;
		}
		case "number": {
			const number__type = root.properties.type.state.node;
			switch (number__type.properties.type.state.name) {
				case "constant": {
					const value = number__type.properties.type.state.node.properties.value.properties.value.value as T;
					return lambda(() => value);
				}
				case "now in seconds":
					return lambda(() => getCurrentDateAndTime() as T);
				case "unary expression": {
					const unary_expression = number__type.properties.type.state.node;
					return ui_scalar_value_expression<number>(unary_expression.properties.expression).pipe(
						step.sync((expression_result) => {
							let value: number;
							switch (unary_expression.properties.type.state.name) {
								case "absolute value":
									value = Math.abs(expression_result);
									break;
								case "numerical type conversion": {
									const $ = unary_expression.properties.type.state.node;
									switch ($.properties.representation_based.state.name) {
										case "no": {
											const to_numerical_type = $.properties.conversion.ref.parent;
											switch (to_numerical_type.properties.ui.properties.represent_as.state.name) {
												case "date":
													value = Math.floor(singular_conversions($.properties.conversion.ref, expression_result));
													break;
												case "date and time":
													value = Math.round(singular_conversions($.properties.conversion.ref, expression_result));
													break;
												case "model":
													value = Math.round(singular_conversions($.properties.conversion.ref, expression_result));
													break;
												case "custom type":
													value = Math.round(singular_conversions($.properties.conversion.ref, expression_result));
													break;
												case "duration":
													value = Math.round(singular_conversions($.properties.conversion.ref, expression_result));
													break;
											}
											break;
										}
										case "ui date and time":
											// Remove the time part of the date
											value = jsdate_2_julian_date(julian_datetime_2_js_date(expression_result));
											break;
									}
									break;
								}
								case "sign inversion":
									value = -expression_result;
									break;
							}
							return value as T;
						})
					);
				}
				case "binary expression": {
					const binary_expression = number__type.properties.type.state.node;
					const value_function = binary_expression.properties.operation.switch({
						division: ($) =>
							$.properties.rounding.switch({
								ordinary: () => (left: number, right: number) => Math.round(left / right),
								ceil: () => (left: number, right: number) => Math.ceil(left / right),
								floor: () => (left: number, right: number) => Math.floor(left / right),
							}),
						remainder: () => (left: number, right: number) => left % right,
					});
					return lambda(($) =>
						ui_scalar_value_expression<number>(binary_expression.properties.expressions.properties.left)
							.and(ui_scalar_value_expression<number>(binary_expression.properties.expressions.properties.right)($))
							.pipe(([left, right]) => {
								if (left === Failed || right === Failed) {
									return Failed;
								} else if (left === Pending || right === Pending) {
									return Pending;
								} else if (left === Unknown || right === Unknown) {
									return Unknown;
								} else {
									return value_function(left, right) as T;
								}
							})($)
					);
				}
				case "list expression": {
					const $ = number__type.properties.type.state.node;
					const value_function = $.properties.operation.switch({
						sum: () => (values: number[]) => values.reduce((result, value) => result + value) as T,
						minimum: () => (values: number[]) => values.reduce((result, value) => Math.min(result, value)) as T,
						maximum: () => (values: number[]) => values.reduce((result, value) => Math.max(result, value)) as T,
						product: () => (values: number[]) => values.reduce((result, value) => result * value) as T,
					});
					return ui_scalar_value_expression_list<number>($.properties.value).pipe(step.sync(value_function));
				}
			}
			break;
		}
		case "state": {
			const state = root.properties.type.state.node.properties.state.ref.switch({
				state: ($) => $ as T,
			});
			return lambda(() => state);
		}
		case "style": {
			const $ = root.properties.type.state.node;
			switch ($.properties.type.state.name) {
				case "fixed": {
					const fixed = $.properties.type.state.node;
					const value = [
						"fixed",
						{
							style: fixed.properties.style.properties.style.state.name,
						},
					] as T;
					return lambda(() => value);
				}
				case "property": {
					const property = $.properties.type.state.node;
					return ui_scalar_value_expression<StringLike>(property.properties.expression).pipe(
						step.sync((result) => {
							return [
								"color",
								{
									value: result.toString(),
								},
							] as T;
						})
					);
				}
			}
			break;
		}
		case "dynamic": {
			const $ = root.properties.type.state.node;
			const property_value = $.properties.property.inferences.value().cast("property value").variant;
			switch (property_value.name) {
				case "number":
					return ui_parametrized_node_path($.properties.path).pipe(
						step.numberValue(property_value.definition.cast("number property"))
					) as DefaultExpressionBuilder<UIContext, Result<T>>;
				case "text":
					return ui_parametrized_node_path($.properties.path).pipe(
						step.textValue(property_value.definition.cast("text property"))
					) as DefaultExpressionBuilder<UIContext, Result<T>>;
				case "state choice":
					return ui_parametrized_node_path($.properties.path)
						.pipe(step.selectedState(property_value.definition.cast("state node choice")))
						.pipe(step.sync((state) => state.key.ref as T));
				case "file":
					return ui_parametrized_node_path($.properties.path).pipe(
						step.fileValue(property_value.definition)
					) as DefaultExpressionBuilder<UIContext, Result<T>>;
			}
			break;
		}
		case "sticky":
			return lambda(([, , target]) => {
				if (target instanceof api_cs.Cproperty) {
					const $type = target.properties.type.state;
					switch ($type.name) {
						case "collection":
							throw new Error("Unexpected context");
						case "file":
							throw new Error("Unexpected context");
						case "group":
							throw new Error("Unexpected context");
						case "number":
							return new StickyProvider<T>(
								() => (getStickyValue($type.node.inferences.number_type()) as T | undefined) ?? Failed
							);
						case "state group": {
							return new StickyProvider<T>(() => {
								const sticky_value = getStickyValue($type.node.inferences.state_group_type());
								if (sticky_value === undefined) return Failed;
								return ($type.node.properties.states.get(sticky_value)?.key.ref as T | undefined) ?? Failed;
							});
						}
						case "text":
							return new StickyProvider<T>(
								() => (getStickyValue($type.node.inferences.text_type()) as T | undefined) ?? Failed
							);
					}
				} else {
					throw new Error("Unexpected context");
				}
			});
	}
}

class StickyProvider<T> extends ScalarProvider<T | Failed> {
	constructor(private fn: () => T | Failed) {
		super();
	}
	get value() {
		return this.fn();
	}
	subscribe(subscriber: ScalarConsumer<T | Failed>): typeof subscriber {
		subscriber.update(this.value);
		return subscriber;
	}
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	unsubscribe() {}
}

function ui_scalar_value_expression_list<T extends ScalarValue>(
	root: api_app.Cui_scalar_value_expression_list
): DefaultExpressionBuilder<UIContext, Result<T[]>> {
	return lambda(($) =>
		ui_scalar_value_expression<T>(root.properties.value).pipe(
			step.sync((result) => {
				switch (root.properties.has_tail.state.name) {
					case "no":
						return [result];
					case "yes": {
						const tail = root.properties.has_tail.state.node.properties.tail;
						return ui_scalar_value_expression_list<T>(tail).pipe(
							step.sync((tail_values) => {
								return [result, ...tail_values];
							})
						)($);
					}
				}
			})
		)($)
	);
}
